import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import CoursePreviewMedia from "components/CoursePreviewMedia";
import LockedContentOverlay from "components/LockedContentOverlay";
import { CoreContext } from "context/CoreContext";
import { openLockedContentModal } from "utils/lockedContent";
import AudienceAccessIndicators from "components/AudienceAccessIndicators";
import { getContentAudiences } from "utils/audienceAccess";
import {
    CardBadge,
    CardContent,
    CardMeta,
    CardMetaChip,
    CardMetaLine,
    CardRoot,
    CardTextBlock,
    CardThumbnail,
    CardTitle,
    PreviewDescription,
    PreviewFloatingCard,
    PreviewFloatingMedia,
    PreviewPortal,
    PreviewPanel,
    PreviewTitle,
} from "./styled";

const PREVIEW_MEDIA_QUERY = "(hover: hover) and (pointer: fine) and (min-width: 992px)";

const normalizeWheelDelta = (event) => {
    if (event.deltaMode === 1) return event.deltaY * 16;
    if (event.deltaMode === 2 && typeof window !== "undefined") return event.deltaY * window.innerHeight;

    return event.deltaY;
};

const isDomNode = (target) => Boolean(target && typeof target === "object" && "nodeType" in target);

const containsNode = (container, target) => {
    if (!container || !isDomNode(target)) return false;

    return container.contains(target);
};

const getPageScrollContainer = (node) => {
    if (typeof window === "undefined" || typeof document === "undefined") return null;

    let parent = node?.parentElement;
    let scrollContainer = null;

    while (parent && parent !== document.body) {
        const style = window.getComputedStyle(parent);
        const canScrollY = /(auto|scroll|overlay)/.test(style.overflowY) && parent.scrollHeight > parent.clientHeight + 1;

        if (canScrollY) {
            scrollContainer = parent;
        }

        parent = parent.parentElement;
    }

    return scrollContainer || document.scrollingElement || document.documentElement;
};

const scrollPageContainer = (node, deltaY) => {
    const scrollContainer = getPageScrollContainer(node);

    if (!scrollContainer) return;

    if (scrollContainer === document.scrollingElement || scrollContainer === document.documentElement) {
        window.scrollBy({ top: deltaY, left: 0, behavior: "auto" });
        return;
    }

    scrollContainer.scrollBy({ top: deltaY, left: 0, behavior: "auto" });
};

function useDesktopPreviewEnabled() {
    const [enabled, setEnabled] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;

        const mediaQuery = window.matchMedia(PREVIEW_MEDIA_QUERY);
        const updateMatches = (event) => {
            setEnabled(Boolean(event?.matches ?? mediaQuery.matches));
        };

        updateMatches();

        if (typeof mediaQuery.addEventListener === "function") {
            mediaQuery.addEventListener("change", updateMatches);

            return () => mediaQuery.removeEventListener("change", updateMatches);
        }

        mediaQuery.addListener(updateMatches);

        return () => mediaQuery.removeListener(updateMatches);
    }, []);

    return enabled;
}

export default function DashboardHomeCard({ item, onAction }) {

    const { setModal } = useContext(CoreContext);
    const locked = item?.accessState === "locked";
    const canPreview = useDesktopPreviewEnabled();
    const cardRef = useRef(null);
    const previewCardRef = useRef(null);
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const [previewHovered, setPreviewHovered] = useState(false);
    const [previewRect, setPreviewRect] = useState(null);
    const wheelScrollRef = useRef({
        deltaY: 0,
        frame: null,
    });

    const hasPreviewVideo = !locked && Boolean(item?.previewVideo?.value);
    const isPreviewActive = canPreview && hasPreviewVideo && (hovered || previewHovered || focused);
    const handleActivation = () => locked ? openLockedContentModal(setModal, item) : onAction(item?.action);
    const handleKeyDown = (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        handleActivation();
    };

    const isWithinInteractivePreview = useCallback((target) => {
        if (!isDomNode(target)) return false;

        return Boolean(
            containsNode(cardRef.current, target) ||
            containsNode(previewCardRef.current, target)
        );
    }, []);

    const handleCardMouseLeave = (event) => {
        const nextTarget = event.relatedTarget;

        if (containsNode(previewCardRef.current, nextTarget)) {
            setHovered(false);
            setPreviewHovered(true);
            return;
        }

        if (!isWithinInteractivePreview(nextTarget)) {
            setHovered(false);
            setPreviewHovered(false);
        }
    };

    const handlePreviewMouseLeave = (event) => {
        const nextTarget = event.relatedTarget;

        if (containsNode(cardRef.current, nextTarget)) {
            setPreviewHovered(false);
            setHovered(true);
            return;
        }

        if (!isWithinInteractivePreview(nextTarget)) {
            setPreviewHovered(false);
            setHovered(false);
        }
    };

    const flushWheelScroll = useCallback(() => {
        const deltaY = wheelScrollRef.current.deltaY;

        wheelScrollRef.current.deltaY = 0;
        wheelScrollRef.current.frame = null;

        if (!deltaY) return;

        scrollPageContainer(cardRef.current, deltaY);
    }, []);

    const queueWheelScroll = useCallback((deltaY) => {
        wheelScrollRef.current.deltaY += deltaY;

        if (wheelScrollRef.current.frame || typeof window === "undefined") return;

        wheelScrollRef.current.frame = window.requestAnimationFrame(flushWheelScroll);
    }, [flushWheelScroll]);

    useEffect(() => {
        if (!isPreviewActive || typeof document === "undefined") return undefined;

        const handleWheelCapture = (event) => {
            if (!isWithinInteractivePreview(event.target)) return;

            const deltaY = normalizeWheelDelta(event);

            if (!deltaY) return;

            event.preventDefault();
            event.stopPropagation();
            queueWheelScroll(deltaY);
        };

        document.addEventListener("wheel", handleWheelCapture, { capture: true, passive: false });

        return () => {
            document.removeEventListener("wheel", handleWheelCapture, { capture: true });
        };
    }, [isPreviewActive, isWithinInteractivePreview, queueWheelScroll]);

    useEffect(() => {
        const wheelScrollState = wheelScrollRef.current;

        return () => {
            if (wheelScrollState.frame && typeof window !== "undefined") {
                window.cancelAnimationFrame(wheelScrollState.frame);
            }
        };
    }, []);

    useEffect(() => {
        if (canPreview && hasPreviewVideo) return;

        setHovered(false);
        setFocused(false);
        setPreviewHovered(false);
    }, [canPreview, hasPreviewVideo]);

    useEffect(() => {
        if (!isPreviewActive || typeof window === "undefined") {
            setPreviewRect(null);
            return undefined;
        }

        const updatePreviewRect = () => {
            if (!cardRef.current) return;

            const rect = cardRef.current.getBoundingClientRect();

            setPreviewRect({
                left: rect.left,
                top: rect.top,
                width: rect.width,
            });
        };

        updatePreviewRect();

        window.addEventListener("resize", updatePreviewRect);
        window.addEventListener("scroll", updatePreviewRect, true);

        return () => {
            window.removeEventListener("resize", updatePreviewRect);
            window.removeEventListener("scroll", updatePreviewRect, true);
        };
    }, [isPreviewActive]);

    const previewPortal = isPreviewActive && previewRect && typeof document !== "undefined" ?
        createPortal(
            <PreviewPortal
                data-testid="dashboard-home-preview-portal"
                style={{
                    left: `${previewRect.left}px`,
                    top: `${Math.max(12, previewRect.top - 10)}px`,
                    width: `${previewRect.width}px`,
                }}
            >
                <PreviewFloatingCard
                    ref={previewCardRef}
                    onClick={() => locked ? openLockedContentModal(setModal, item) : onAction(item?.action)}
                    onMouseEnter={() => {
                        setPreviewHovered(true);
                    }}
                    onMouseLeave={handlePreviewMouseLeave}
                >
                    <PreviewFloatingMedia>
                        <CoursePreviewMedia
                            active
                            imageUrl={item?.imageUrl}
                            previewVideo={item?.previewVideo}
                            title={item?.title}
                        />
                        {item?.badge ? <CardBadge>{item.badge}</CardBadge> : null}
                        {item?.type === "courses" ? null : (
                            <AudienceAccessIndicators audiences={getContentAudiences(item)} />
                        )}
                    </PreviewFloatingMedia>

                    <PreviewPanel active>
                        <CardTextBlock>
                            <PreviewTitle>{item?.title}</PreviewTitle>
                            {item?.description ? <PreviewDescription>{item.description}</PreviewDescription> : null}
                            {item?.category || item?.badge ? (
                                <CardMeta>
                                    {item?.category ? <CardMetaLine>{item.category}</CardMetaLine> : null}
                                    {item?.badge ? <CardMetaChip>{item.badge}</CardMetaChip> : null}
                                </CardMeta>
                            ) : null}
                        </CardTextBlock>
                    </PreviewPanel>
                </PreviewFloatingCard>
            </PreviewPortal>,
            document.body
        ) : null;

    return (
        <>
            <CardRoot
                ref={cardRef}
                active={isPreviewActive}
                onBlur={() => setFocused(false)}
                onClick={handleActivation}
                onFocus={() => setFocused(true)}
                onKeyDown={handleKeyDown}
                onMouseEnter={() => locked ? null : setHovered(true)}
                onMouseLeave={handleCardMouseLeave}
                aria-disabled={locked}
                aria-label={locked ? `${item?.title || "Content"}. Exclusive content` : `Open ${item?.title || "content"}`}
            >
                <CardThumbnail active={isPreviewActive}>
                    <CoursePreviewMedia
                        active={false}
                        imageUrl={item?.imageUrl}
                        previewVideo={item?.previewVideo}
                        title={item?.title}
                    />
                    {item?.badge ? <CardBadge>{item.badge}</CardBadge> : null}
                    {item?.type === "courses" ? null : (
                        <AudienceAccessIndicators audiences={getContentAudiences(item)} />
                    )}
                    {locked ? <LockedContentOverlay /> : null}
                </CardThumbnail>

                <CardContent active={isPreviewActive}>
                    <CardTextBlock>
                        <CardTitle>{item?.title}</CardTitle>
                        {item?.category || item?.badge ? (
                            <CardMeta>
                                {item?.category ? <CardMetaLine>{item.category}</CardMetaLine> : null}
                                {item?.badge ? <CardMetaChip>{item.badge}</CardMetaChip> : null}
                            </CardMeta>
                        ) : null}
                    </CardTextBlock>
                </CardContent>
            </CardRoot>
            {previewPortal}
        </>
    );
}
